/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { graphql } from 'react-apollo';

import { branch, compose, withProps } from 'recompose';

import { getCmsLocaleString } from 'js/lib/language';

import { Grid, breakpoints, useMediaQuery } from '@coursera/cds-core';
import type { Recommender } from '@coursera/event-pulse-types';

import usePrevious from 'bundles/common/hooks/usePrevious';
import useSubfolderLanguageAndLocale from 'bundles/internationalization-lib/hooks/useSubfolderLanguageAndLocale';
import useUserAgent from 'bundles/page/hooks/useUserAgent';
import type { CardProps } from 'bundles/product-card/components/types/ProductCardV2';
import type {
  AllExtraMetadataQuery,
  AllExtraMetadataQueryVariables,
} from 'bundles/search-common/__generated__/contentfulTypes';
import { ProductsCardsCdsPlaceholder } from 'bundles/search-common/components/search-cards/Placeholder';
import SearchCard from 'bundles/search-common/components/search-cards/SearchCard';
import isPremiumProduct from 'bundles/search-common/components/search-cards/isPremiumProduct';
import {
  LEARNING_PRODUCTS_ENUM_MAP,
  PRODUCT_DURATIONS_STRING,
  getTranslatedDifficulty,
  getTranslatedDurations,
} from 'bundles/search-common/constants';
import type { LEARNING_PRODUCTS, PRODUCT_DIFFICULTY_LEVELS } from 'bundles/search-common/constants';
import { useOwnsCourseraLite, useOwnsCourseraPlus } from 'bundles/search-common/providers/LearnerProvider';
import type {
  NoResultsSearchPageEventingData,
  SearchHit,
  SearchProductHit,
  SearchUpsellHit,
} from 'bundles/search-common/providers/searchTypes';
import getAllExtraMetadataQuery from 'bundles/search-common/queries/getAllExtraMetadataQuery';
import type { SearchIndexHit } from 'bundles/search-common/types';
import { getAvgRating, getIsPathwayContent, getNumOfRatings } from 'bundles/search-common/utils/SearchUtils';
import {
  getShouldShowProductCardDensityRating,
  getShouldShowProductCardDensitySkills,
  isInFilterTestVariant,
} from 'bundles/search-common/utils/experimentUtils';
import getProductPageUrlFromHit from 'bundles/search-common/utils/getProductPageUrlFromHit';
import useIsEqp from 'bundles/search-common/utils/useIsEqp';
import { isSearchProductHit, isSearchUpsellHit, scrollUpIfNecessary } from 'bundles/search-common/utils/utils';
import CourseraPlusUpsellCard from 'bundles/unified-common/components/coursera-plus-upsells/CourseraPlusUpsellCard';
import useEnableSubfolderLinksForXDP from 'bundles/unified-description-page-common/hooks/useEnableSubfolderLinksForXDP';

const MOBILE_CARD_IMAGE_PARAMS = { HEIGHT: 216, WIDTH: 265 };

const styles = {
  container: css`
    list-style: none;
    padding-left: 0;
  `,
  /* TrackedLink2's visibility tracking adds an extra div that interferes with the search card styling.
  This wrapper div applies style to that extra div to ensure the search cards are styled correctly. */
  targetSearchCardWrapper: css`
    height: 100%;
    padding: 0 6px 24px;

    ${breakpoints.down('xs')} {
      padding: 0 0 24px 0;
    }

    .cds-CommonCard-previewImage > div {
      padding: 0;
      height: 100%;
    }

    .cds-CommonCard-previewImage .lazyload-wrapper {
      height: 100%;
    }

    .cds-ProductCard-list {
      /* temporaryly force list card to have border */
      border: 2px solid var(--cds-color-grey-50);
    }
  `,
  upsellCardWrapper: css`
    height: 100%;
    padding: 0 6px 24px;
    ${breakpoints.between('sm', 'md')} {
      max-width: 465px;
    }
    ${breakpoints.up('sm')} {
      min-height: 450px;
    }
  `,
};

type PremiumProductMetadata = {
  slug?: string;
  atAGlance?: { timeFrame?: string };
};

type PropsFromWithProps = {
  hitsWithSlug: { slug: string | undefined }[];
};

type InputProps = {
  hits?: Array<SearchHit | SearchUpsellHit>;
  forceOpenInNewTab?: boolean;
  noResults?: boolean;
  resultsPerPage: number;
  isSearchPage?: boolean;
  isDegreePage?: boolean;
  eventingData?: {
    searchIndex: string;
    searchRecommender?: Recommender;
    searchIndexPosition: number;
    page?: string;
    searchQuery?: string;
    filtersApplied?: Record<string, string[]>;
  };
  searchCardImageProps?: { fit: string };
  isLoading?: boolean;
  onScrolledToBottom?: () => void;
  totalHitsDisplayed?: number;
  cardIndexOffset?: number;
  noSearchResultsCollectionData?: NoResultsSearchPageEventingData;
  isEndOfSplitSection?: boolean;
};

type PropsToComponent = InputProps;

export const PREMIUM_PRODUCT_TO_DURATION: Record<string, string | undefined> = {};

export const getHitSlug = (objectUrl?: string | null) => {
  if (!objectUrl) return undefined;
  return objectUrl.substring(objectUrl.lastIndexOf('/') + 1);
};

const addDurationToPremiumProduct = (premiumProduct: PremiumProductMetadata) => {
  PREMIUM_PRODUCT_TO_DURATION[premiumProduct.slug as string] = premiumProduct.atAGlance?.timeFrame;
};

export const combineHitData = (hits: SearchIndexHit[]) =>
  hits.map((hit) => {
    const hitSlug = getHitSlug(hit.objectUrl);

    if (hitSlug && PREMIUM_PRODUCT_TO_DURATION[hitSlug]) {
      // Combine Algolia and Contentful data for premium products
      return {
        ...hit,
        productDuration: PREMIUM_PRODUCT_TO_DURATION[hitSlug],
      };
    }
    return hit;
  });

export const convertHitToCardProps = (
  hit: SearchProductHit,
  hitPosition: number,
  isSearchPage: boolean,
  isSEOEntityPage: boolean,
  cardIndexOffset: number
): CardProps => {
  const hitSlug = getHitSlug(hit.url);
  let productDuration = getTranslatedDurations(PRODUCT_DURATIONS_STRING[hit.productDuration || '']);
  const shouldShowProductCardDensitySkills = isSearchPage && getShouldShowProductCardDensitySkills();
  const shouldShowProductCardDensityRating = isSearchPage && getShouldShowProductCardDensityRating();
  if (hitSlug && PREMIUM_PRODUCT_TO_DURATION[hitSlug]) {
    productDuration = PREMIUM_PRODUCT_TO_DURATION[hitSlug];
  }
  const shouldTranslateProduct = isSEOEntityPage || isSearchPage;
  return {
    id: hit.id || undefined,
    name: (shouldTranslateProduct && hit.translatedName) || hit.name || undefined,
    imageUrl: hit.imageUrl || undefined,
    productType: LEARNING_PRODUCTS_ENUM_MAP[hit.productType || ''],
    productDifficultyLevel: getTranslatedDifficulty(
      hit.productDifficultyLevel || ''
    ) as (typeof PRODUCT_DIFFICULTY_LEVELS)[keyof typeof PRODUCT_DIFFICULTY_LEVELS],
    productDuration: productDuration || undefined,
    partners:
      (hit.partners &&
        hit.partners.map((partner, index) => {
          return { id: index.toString(), logo: undefined, name: partner };
        })) ||
      undefined,
    partnerLogos: hit.partnerLogos || undefined,
    skills: shouldShowProductCardDensitySkills
      ? undefined
      : (shouldTranslateProduct && hit.translatedSkills) || hit.skills || undefined,
    isCostFree: hit.isCourseFree || undefined,
    isPartOfCourseraPlus: hit.isPartOfCourseraPlus || undefined,
    isNewContent: hit.isNewContent || undefined,
    isCreditEligible: hit.isCreditEligible || undefined,
    avgProductRating: shouldShowProductCardDensityRating ? undefined : hit.avgProductRating || getAvgRating(hit),
    numProductRatings: hit.numProductRatings || getNumOfRatings(hit),
    isPathwayContent: getIsPathwayContent(hit),
    // session: hit.session, // TODO: only used for enterprise
    cobrandingEnabled: hit.cobrandingEnabled || undefined,
    cardPosition: cardIndexOffset + hitPosition,
    slug: hitSlug,
  };
};

const SearchCards = ({
  hits,
  forceOpenInNewTab,
  noResults = false,
  resultsPerPage,
  eventingData,
  searchCardImageProps,
  isLoading,
  isSearchPage,
  isDegreePage,
  onScrolledToBottom,
  totalHitsDisplayed,
  cardIndexOffset = 0,
  noSearchResultsCollectionData,
  isEndOfSplitSection,
}: PropsToComponent) => {
  const userAgent = useUserAgent();
  const ownsCourseraLite = useOwnsCourseraLite();
  const ownsCourseraPlus = useOwnsCourseraPlus();
  const isEqp = useIsEqp();
  const isMobileOrTabletView = useMediaQuery(breakpoints.down('md'));
  const isMobileView = useMediaQuery(breakpoints.down('xs'), { defaultMatches: userAgent?.isMobileBrowser });
  const subfolderLanguageAndLocale = useSubfolderLanguageAndLocale();
  const enableSubfolderLinksForXDP = useEnableSubfolderLinksForXDP();
  const productCardVariant = isMobileView ? 'list' : 'grid';

  // /search is the only location with infinite scroll pagination
  const isTraditionalPagination = !isSearchPage;
  const infiniteScrollTriggerRef = useRef<HTMLDivElement>(null);
  const [hasTriggeredMap, setHasTriggeredMap] = useState<Map<number, boolean>>(new Map());
  const previousHitsCount = usePrevious(totalHitsDisplayed || 0);
  const previousQuery = usePrevious(eventingData?.searchQuery || '');
  const [windowIsSafe, setWindowIsSafe] = useState(false);
  const hasScrolledBottomDetectorIndex = useMemo(() => {
    const targetTriggerIndex = (hits?.length || 0) - 1;
    const triggerShouldBeFound = !hasTriggeredMap.get(targetTriggerIndex) && !!onScrolledToBottom && !isLoading;
    return triggerShouldBeFound ? targetTriggerIndex : -1;
  }, [hits?.length, hasTriggeredMap, onScrolledToBottom, isLoading]);

  const handleScrollBottom = useCallback<([trigger]: IntersectionObserverEntry[]) => void>(
    ([trigger]: IntersectionObserverEntry[]) => {
      const shouldCallback =
        !isLoading && trigger?.isIntersecting && !hasTriggeredMap.get(hasScrolledBottomDetectorIndex);

      if (shouldCallback) {
        setHasTriggeredMap(new Map(hasTriggeredMap.set(hasScrolledBottomDetectorIndex, true)));
        onScrolledToBottom?.();
      }
    },
    [hasScrolledBottomDetectorIndex, hasTriggeredMap, isLoading, onScrolledToBottom]
  );

  const infiniteScrollObserver = useMemo(() => {
    const shouldHaveObserver = onScrolledToBottom && windowIsSafe;

    return shouldHaveObserver
      ? new IntersectionObserver(handleScrollBottom, {
          threshold: [0, 1.0],
        })
      : false;
  }, [handleScrollBottom, onScrolledToBottom, windowIsSafe]);

  useEffect(() => {
    const hasNewQuery = !!previousQuery && previousQuery !== eventingData?.searchQuery;
    // using totalHits as a proxy heuristic for when a new search has executed in order to
    // reset the infinite scroll trigger. should be cleaned up if experiment is successful.
    const displayedHitsCountShrank = (totalHitsDisplayed || 0) < (previousHitsCount || 0);
    if (displayedHitsCountShrank && !isTraditionalPagination) {
      setHasTriggeredMap(new Map());
    }

    if (hasNewQuery) {
      scrollUpIfNecessary({
        prefersReducedMotion: !!window?.matchMedia(`(prefers-reduced-motion: reduce)`)?.matches === true,
        mustBeScrolledPastThisElementId: 'main',
      });
    }

    if (typeof window !== 'undefined' && 'IntersectionObserver' in window) {
      setWindowIsSafe(true);
    }

    if (infiniteScrollObserver && infiniteScrollTriggerRef.current) {
      infiniteScrollObserver.observe(infiniteScrollTriggerRef.current);
    }

    return () => {
      if (infiniteScrollObserver) infiniteScrollObserver.disconnect();
    };
  }, [
    infiniteScrollTriggerRef,
    totalHitsDisplayed,
    previousHitsCount,
    isTraditionalPagination,
    infiniteScrollObserver,
    previousQuery,
    eventingData?.searchQuery,
    isMobileOrTabletView,
  ]);
  // end experimental pagination related

  if ((isLoading && isTraditionalPagination) || !hits) {
    return <ProductsCardsCdsPlaceholder resultsPerPage={resultsPerPage} variant={productCardVariant} />;
  }

  const trackingName = noResults ? 'no_results_search_card' : 'search_card';

  const shouldUseSubfolderLinksForXDP = isEqp && enableSubfolderLinksForXDP;
  const isInFilterExptTestVar = isInFilterTestVariant() && isSearchPage;
  const desktopCardSize = isInFilterExptTestVar ? 3 : 4;

  return (
    <>
      <Grid
        container
        component="ul"
        css={[styles.container, (!isTraditionalPagination || !isEndOfSplitSection) && { marginBottom: '0' }]}
      >
        {hits.map((hit, hitIndex) => {
          if (!isSearchProductHit(hit) && !isSearchUpsellHit(hit)) return null;

          if (isSearchUpsellHit(hit)) {
            return (
              <Grid key={hit.id} item lg={desktopCardSize} md={4} sm={6} xs={12} component="li">
                <div
                  css={[styles.upsellCardWrapper]}
                  ref={hitIndex === hasScrolledBottomDetectorIndex ? infiniteScrollTriggerRef : null}
                >
                  <CourseraPlusUpsellCard
                    title={hit.title}
                    mobileBreakpoint={hit.breakpoint}
                    eventingData={{ index: hitIndex }}
                    forceOpenInNewTab={forceOpenInNewTab}
                  />
                </div>
              </Grid>
            );
          }
          const cardProps = convertHitToCardProps(hit, hitIndex, !!isSearchPage, !!isEqp, cardIndexOffset);

          return (
            <Grid key={hit.id} item lg={desktopCardSize} md={4} sm={6} xs={12} component="li">
              <div
                css={[styles.targetSearchCardWrapper]}
                ref={hitIndex === hasScrolledBottomDetectorIndex ? infiniteScrollTriggerRef : null}
              >
                <SearchCard
                  name={cardProps.name}
                  imageUrl={cardProps.imageUrl}
                  imageProps={
                    isMobileOrTabletView
                      ? {
                          ...searchCardImageProps,
                          height: MOBILE_CARD_IMAGE_PARAMS.HEIGHT,
                          width: MOBILE_CARD_IMAGE_PARAMS.WIDTH,
                        }
                      : searchCardImageProps
                  }
                  objectUrl={getProductPageUrlFromHit({
                    hit,
                    shouldUseSubfolderLinksForXDP,
                    subfolderLocaleCode: subfolderLanguageAndLocale,
                  })}
                  slug={cardProps.slug}
                  productType={cardProps.productType}
                  productDifficultyLevel={cardProps.productDifficultyLevel}
                  productDuration={cardProps.productDuration}
                  partners={cardProps.partners}
                  partnerLogos={cardProps.partnerLogos}
                  coBrandingEnabled={cardProps.cobrandingEnabled}
                  skills={cardProps.skills}
                  avgProductRatings={cardProps.avgProductRating}
                  numProductRatings={cardProps.numProductRatings}
                  isCostFree={cardProps.isCostFree}
                  isNewContent={cardProps.isNewContent}
                  isPartOfCourseraPlus={cardProps.isPartOfCourseraPlus}
                  ownsCourseraLite={ownsCourseraLite}
                  ownsCourseraPlus={ownsCourseraPlus}
                  cardPosition={cardProps.cardPosition}
                  shouldPrioritizeImageLoading={
                    (isMobileOrTabletView && (hitIndex ?? 10) < 2) || (!isMobileOrTabletView && (hitIndex ?? 10) < 6)
                  }
                  variant={productCardVariant}
                  trackedLinkProps={{
                    trackingName,
                    withVisibilityTracking: true,
                    requireFullyVisible: true,
                    data: {
                      objectID: hit.id || undefined,
                      hitPosition: cardIndexOffset + hitIndex,
                      queryID: eventingData?.searchQuery || '',
                      query: eventingData?.searchQuery || '',
                      listIndex: Number(eventingData?.page) || 1,
                      noResults,
                      filtersApplied: eventingData?.filtersApplied,
                      indexName: eventingData?.searchIndex,
                      indexPosition: eventingData?.searchIndexPosition,
                      ...noSearchResultsCollectionData,
                    },
                  }}
                  id={hit?.id || ''}
                  recommender={eventingData?.searchRecommender}
                  indexName={eventingData?.searchIndex}
                  searchedQuery={eventingData?.searchQuery || ''}
                  forceOpenInNewTab={forceOpenInNewTab}
                  isPathwayContent={cardProps.isPathwayContent}
                />
              </div>
            </Grid>
          );
        })}
      </Grid>
      {isLoading && !isTraditionalPagination && (
        <ProductsCardsCdsPlaceholder resultsPerPage={resultsPerPage} variant={productCardVariant} />
      )}
    </>
  );
};

// Only call contentful if the slug is defined and the data is not already in the global object
export const filterPremiumHits = ({ hits }: { hits?: Array<SearchHit | SearchUpsellHit> }) => {
  const premiumProducts =
    hits?.filter(
      (hit) =>
        hit?.__typename === 'Search_ProductHit' &&
        hit?.productType &&
        isPremiumProduct(hit?.productType as (typeof LEARNING_PRODUCTS)[keyof typeof LEARNING_PRODUCTS])
    ) || [];

  const hitsWithSlug = premiumProducts
    .map((hit) => ({ slug: isSearchProductHit(hit) ? getHitSlug(hit.url || undefined) : undefined }))
    .filter((hit) => !!hit.slug && !(hit.slug in PREMIUM_PRODUCT_TO_DURATION));

  return { hitsWithSlug };
};

/**
 * TODO: REMOVE THIS GRAPHGL CALL!!!
 * THIS BREAKS THE RULES WE DEFINED FOR UNIFIED PLATFORM
 * This graph call should be removed as part of (https://coursera.atlassian.net/browse/GR-28924
 * and https://coursera.atlassian.net/browse/SEARCH-1254)
 */
export default memo(
  compose<PropsToComponent, InputProps>(
    withProps<PropsFromWithProps, InputProps>(filterPremiumHits),
    branch<PropsFromWithProps>(
      ({ hitsWithSlug }) => hitsWithSlug.length > 0,
      graphql<PropsFromWithProps & InputProps, AllExtraMetadataQuery, AllExtraMetadataQueryVariables, {}>(
        getAllExtraMetadataQuery,
        {
          options: ({ hitsWithSlug }) => ({
            variables: {
              degreeSlugs: hitsWithSlug,
              mastertrackSlugs: hitsWithSlug,
              locale: getCmsLocaleString(),
            },
            context: { clientName: 'contentfulGql' },
            fetchPolicy: 'cache-first',
            ssr: false,
          }),
          props: ({ data }) => {
            const degreesData = data?.universityProgramDegreeDescriptionPageCollection?.items;
            const mastertrackData = data?.premiumCertificatePageContentCollection?.items;

            if (degreesData) {
              degreesData.forEach((degree) => addDurationToPremiumProduct(degree as PremiumProductMetadata));
            }

            if (mastertrackData) {
              mastertrackData.forEach((mastertrack) =>
                addDurationToPremiumProduct(mastertrack as PremiumProductMetadata)
              );
            }
            return {};
          },
        }
      )
    )
  )(SearchCards)
);
